import { useEffect, useRef } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Traps keyboard focus within the referenced container while `active` is true.
 * On activation, moves focus to the first focusable child and records the
 * previously-focused element. On deactivation, restores focus to that element.
 * Tab / Shift+Tab wraps at the boundaries.
 * Initial focus, visible Tab targets and restoration follow the modal pattern.
 */
export function useFocusTrap(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !ref.current) return;

    // Remember where focus was before opening so we can restore it on close
    const trigger = document.activeElement as HTMLElement | null;

    const el = ref.current;
    // Make background branches inert without making an ancestor of the
    // dialog inert. Restore pre-existing inert state when the dialog closes.
    const background: Array<[HTMLElement, boolean]> = [];
    let branch: HTMLElement = el;
    while (branch.parentElement && branch.parentElement !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && sibling instanceof HTMLElement) {
          background.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
      }
      branch = branch.parentElement;
    }
    const focusable = () =>
      Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => !n.closest("[inert]") && n.getClientRects().length > 0 && getComputedStyle(n).visibility !== "hidden",
      );

    // Move initial focus to the first focusable element
    el.tabIndex = -1;
    el.focus({ preventScroll: true });

    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const nodes = focusable();
      if (!nodes.length) { e.preventDefault(); el.focus(); return; }

      // Move focus explicitly rather than letting the browser do it, and wrap
      // by index. The earlier version only intercepted Tab at the boundaries
      // and compared `document.activeElement` against a list it recomputed on
      // every keypress — so if the dialog's content changed between two
      // presses (a feed resolving, a tile appearing), the focused element was
      // no longer the last one, the boundary was missed, and native Tab walked
      // focus straight out of the dialog. Deriving the next target from the
      // current index makes the cycle deterministic whatever the DOM does.
      const current = nodes.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey
        ? (current <= 0 ? nodes.length - 1 : current - 1)
        : (current < 0 || current === nodes.length - 1 ? 0 : current + 1);
      e.preventDefault();
      nodes[next].focus();
    };

    window.addEventListener("keydown", trap);
    return () => {
      window.removeEventListener("keydown", trap);
      // Restore focus to the trigger element when modal closes
      for (const [node, wasInert] of background) node.inert = wasInert;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [active]);

  return ref;
}
